/**
 * AdminMeetings — /admin/meetings. Unified audit table across every
 * user's meetings in every status, with bulk operations + audit log
 * viewer.
 *
 * Columns:
 *   - Checkbox for bulk selection
 *   - Meeting (title + updated)
 *   - Date (moved to 2nd per admin pref — primary scan field)
 *   - Project
 *   - Owner (avatar + name + email)
 *   - Status (pill)
 *   - Cost (sum of usage_log.cost_usd per meeting)
 *   - Actions (Open / Restore / View audit)
 *
 * Filters:
 *   - Status chips (driven by server-side counts, clickable)
 *   - Title search
 *   - Owner typeahead (from /admin/users)
 *   - Project typeahead (from /projects)
 *   - Preset date chips: This month / Last 7d / Last 30d / All
 *   - From / To custom dates
 *
 * UX:
 *   - Infinite scroll via useInfiniteQuery + IntersectionObserver on
 *     a sentinel row at the bottom of the table
 *   - Click column headers to sort (server-side; supported keys:
 *     title, meetingDate, costUsd, updatedAt)
 *   - Bulk action bar appears when any row is selected: restore /
 *     transfer / soft-delete, all with audit_log writes
 *   - Per-meeting audit log in a modal (who restored / transferred /
 *     soft-deleted, when)
 *
 * Per the product brief no auto-refresh here — meeting state is
 * stable enough to not poll, and auto-refetch while someone's
 * cursor is on a row they're about to act on would be disorienting.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { AttendeeAvatar } from '../../components/editor/AttendeeAvatar.tsx';

interface AdminMeeting {
  id: string;
  title: string;
  meetingDate: string;
  status: 'draft' | 'finalized' | 'soft_deleted';
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerHeadshotUrl: string | null;
  projectId: string;
  projectName: string | null;
  finalizedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  costUsd: number;
}

interface AdminMeetingsResponse {
  meetings: AdminMeeting[];
  statusCounts: { draft: number; finalized: number; soft_deleted: number };
  nextOffset: number | null;
}

type StatusFilter = 'all' | 'draft' | 'finalized' | 'soft_deleted';
type SortKey = 'title' | 'meetingDate' | 'costUsd' | 'updatedAt';
type SortDir = 'asc' | 'desc';

export function AdminMeetings() {
  const qc = useQueryClient();

  // ── Filter state ────────────────────────────────────────────────
  const [status, setStatus] = useState<StatusFilter>('all');
  const [q, setQ] = useState('');
  const [ownerUserId, setOwnerUserId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // ── Selection (for bulk actions) ────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  // Selections should clear any time filters change to avoid acting
  // on stale ids the user can't see anymore.
  useEffect(() => {
    clearSelection();
  }, [status, q, ownerUserId, projectId, from, to, clearSelection]);

  // ── Modal state ─────────────────────────────────────────────────
  const [auditForId, setAuditForId] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);

  // ── Data (infinite query) ───────────────────────────────────────
  const list = useInfiniteQuery<AdminMeetingsResponse>({
    queryKey: [
      'admin',
      'meetings',
      { status, q, ownerUserId, projectId, from, to, sortKey, sortDir },
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      api.get<AdminMeetingsResponse>('/admin/meetings', {
        status,
        q: q.trim() || undefined,
        ownerUserId: ownerUserId || undefined,
        projectId: projectId || undefined,
        from: from || undefined,
        to: to || undefined,
        sort: sortKey,
        dir: sortDir,
        limit: 50,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  });

  const allRows = useMemo(
    () => list.data?.pages.flatMap((p) => p.meetings) ?? [],
    [list.data],
  );
  const statusCounts =
    list.data?.pages[0]?.statusCounts ?? {
      draft: 0,
      finalized: 0,
      soft_deleted: 0,
    };

  // ── IntersectionObserver for infinite scroll ────────────────────
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          list.hasNextPage &&
          !list.isFetchingNextPage
        ) {
          list.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [list]);

  // ── Mutations ────────────────────────────────────────────────────
  const restoreOne = useMutation({
    mutationFn: (id: string) =>
      api.post<{ meeting: AdminMeeting }>(`/admin/meetings/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'meetings'] });
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
  const bulkRestore = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ restored: number; skipped: number }>(
        '/admin/meetings/bulk-restore',
        { ids },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'meetings'] });
      clearSelection();
    },
  });
  const bulkSoftDelete = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number; skipped: number }>(
        '/admin/meetings/bulk-soft-delete',
        { ids },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'meetings'] });
      clearSelection();
    },
  });
  const bulkTransfer = useMutation({
    mutationFn: (args: { ids: string[]; newOwnerUserId: string }) =>
      api.post<{ transferred: number; skipped: number }>(
        '/admin/meetings/bulk-transfer',
        args,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'meetings'] });
      clearSelection();
      setTransferOpen(false);
    },
  });

  // ── Helpers ─────────────────────────────────────────────────────
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === allRows.length ? new Set() : new Set(allRows.map((r) => r.id)),
    );
  };
  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Numeric + date columns descend first; text ascends first.
      setSortDir(key === 'title' ? 'asc' : 'desc');
    }
  };
  const hasFilters =
    q || from || to || ownerUserId || projectId || status !== 'all';
  const clearAll = () => {
    setStatus('all');
    setQ('');
    setOwnerUserId('');
    setProjectId('');
    setFrom('');
    setTo('');
  };
  const applyPreset = (preset: 'this-month' | 'last-7' | 'last-30' | 'all') => {
    if (preset === 'all') {
      setFrom('');
      setTo('');
      return;
    }
    const now = new Date();
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const today = iso(now);
    if (preset === 'this-month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      setFrom(iso(start));
      setTo(today);
    } else if (preset === 'last-7') {
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      setFrom(iso(start));
      setTo(today);
    } else if (preset === 'last-30') {
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setFrom(iso(start));
      setTo(today);
    }
  };

  const selectedIds = [...selected];

  return (
    <div className="space-y-4">
      {/* Status count chips — click to filter */}
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip
          label="All"
          count={
            statusCounts.draft + statusCounts.finalized + statusCounts.soft_deleted
          }
          active={status === 'all'}
          onClick={() => setStatus('all')}
          tone="default"
        />
        <StatusChip
          label="Drafts"
          count={statusCounts.draft}
          active={status === 'draft'}
          onClick={() => setStatus('draft')}
          tone="default"
        />
        <StatusChip
          label="Finalized"
          count={statusCounts.finalized}
          active={status === 'finalized'}
          onClick={() => setStatus('finalized')}
          tone="pine"
        />
        <StatusChip
          label="Deleted"
          count={statusCounts.soft_deleted}
          active={status === 'soft_deleted'}
          onClick={() => setStatus('soft_deleted')}
          tone="red"
        />
      </div>

      {/* Filter card — mockup .filter-card: cloud + charcoal/10 +
          rounded-[14px] + 14/16 padding. Inputs use .filter-card__input
          chrome (bone bg + charcoal/20 + rounded-[8px]). */}
      <div className="space-y-2.5 rounded-[14px] border border-charcoal/10 bg-cloud px-4 py-3.5">
        <div className="flex flex-wrap gap-3">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-[11px]">
            <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              Title search
            </span>
            <input
              {...NO_AUTOFILL}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Duke Park, IT sync, …"
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            />
          </label>
          <OwnerPicker value={ownerUserId} onChange={setOwnerUserId} />
          <ProjectPicker value={projectId} onChange={setProjectId} />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[11px]">
            <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              From
            </span>
            <input
              {...NO_AUTOFILL}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            />
          </label>
          <label className="flex flex-col gap-1 text-[11px]">
            <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              To
            </span>
            <input
              {...NO_AUTOFILL}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            />
          </label>
          <div className="flex flex-wrap items-center gap-1.5 pb-1">
            <PresetChip onClick={() => applyPreset('this-month')}>
              This month
            </PresetChip>
            <PresetChip onClick={() => applyPreset('last-7')}>Last 7d</PresetChip>
            <PresetChip onClick={() => applyPreset('last-30')}>Last 30d</PresetChip>
            <PresetChip onClick={() => applyPreset('all')}>All dates</PresetChip>
          </div>
          {hasFilters && (
            // Mockup .clear-link: 11px charcoal/55 underline,
            // hover pine.
            <button
              type="button"
              onClick={clearAll}
              className="pb-1 text-[11px] text-charcoal/55 underline transition-colors hover:text-pine"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar — mockup .bulk-bar: pine-soft + pine/30 +
          rounded-[14px] + 10/18 padding. Three pine-outlined buttons
          (Restore / Transfer / Soft-delete) plus a clear-link. The
          Soft-delete button uses the --delete variant (terra-cotta
          outline + hover terra-tint). */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-[14px] border border-pine/30 bg-pine/[0.06] px-[18px] py-2.5 text-[13px]">
          <div className="mr-auto font-medium text-pine">
            <b className="font-bold">{selectedIds.length}</b> selected
          </div>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Restore ${selectedIds.length} meeting(s) to draft?`)) {
                bulkRestore.mutate(selectedIds);
              }
            }}
            disabled={bulkRestore.isPending}
            className="rounded-full border border-pine bg-transparent px-3 py-1 text-[11px] font-medium text-pine transition-colors hover:bg-pine/[0.08] disabled:opacity-50"
          >
            Restore
          </button>
          <button
            type="button"
            onClick={() => setTransferOpen(true)}
            className="rounded-full border border-pine bg-transparent px-3 py-1 text-[11px] font-medium text-pine transition-colors hover:bg-pine/[0.08]"
          >
            Transfer owner
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Soft-delete ${selectedIds.length} meeting(s)? They can be restored.`,
                )
              ) {
                bulkSoftDelete.mutate(selectedIds);
              }
            }}
            disabled={bulkSoftDelete.isPending}
            className="rounded-full border border-terra-cotta/50 bg-transparent px-3 py-1 text-[11px] font-medium text-terra-cotta transition-colors hover:bg-terra-cotta/[0.08] disabled:opacity-50"
          >
            Soft-delete
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[11px] text-charcoal/50 underline transition-colors hover:text-pine"
          >
            Clear
          </button>
        </div>
      )}

      {/* States */}
      {list.isLoading && (
        <div className="py-8 text-center text-[13px] italic text-charcoal/55">
          Loading…
        </div>
      )}
      {list.error && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 p-4 text-[13px] text-terra-cotta">
          {String((list.error as Error).message)}
        </div>
      )}

      {allRows.length === 0 && !list.isLoading && !list.error && (
        <div className="rounded-[14px] border border-dashed border-charcoal/20 bg-cloud/40 p-10 text-center text-[12px] italic text-charcoal/55">
          No meetings match these filters.
        </div>
      )}

      {/* Table — mockup .admin-table: bone bg + charcoal/8 +
          rounded-[14px] + cloud/60 thead with 10px tracked-uppercase
          headers. Same chrome family as AdminUsers. */}
      {allRows.length > 0 && (
        <div className="overflow-x-auto rounded-[14px] border border-charcoal/8 bg-bone shadow-[0_1px_2px_rgba(43,42,47,0.04)]">
          <table className="w-full text-[13px]">
            <thead className="border-b border-charcoal/10 bg-cloud/60 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-charcoal/65">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.length > 0 &&
                      selectedIds.length === allRows.length
                    }
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedIds.length > 0 &&
                          selectedIds.length < allRows.length;
                      }
                    }}
                    onChange={toggleAll}
                    className="accent-pine"
                    aria-label="Select all"
                  />
                </th>
                <Th sortKey="title" current={sortKey} dir={sortDir} onSort={onSort}>
                  Meeting
                </Th>
                <Th
                  sortKey="meetingDate"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                >
                  Date
                </Th>
                <th className="px-3.5 py-2.5">Project</th>
                <th className="px-3.5 py-2.5">Owner</th>
                <th className="px-3.5 py-2.5">Status</th>
                <Th
                  sortKey="costUsd"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  align="right"
                >
                  Cost
                </Th>
                <th className="px-3.5 py-2.5" />
              </tr>
            </thead>
            {/* Per-row border + hover bg matches mockup
                .admin-table tbody tr (charcoal/[0.06] divider +
                pine/[0.04] hover wash). */}
            <tbody>
              {allRows.map((m) => (
                <tr
                  key={m.id}
                  className={
                    'border-b border-charcoal/[0.06] last:border-b-0 transition-colors hover:bg-pine/[0.04] ' +
                    (m.status === 'soft_deleted' ? 'opacity-60 ' : '') +
                    (selected.has(m.id) ? 'bg-pine/[0.05] ' : '')
                  }
                >
                  <td className="px-3.5 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggleOne(m.id)}
                      className="accent-pine"
                      aria-label="Select meeting"
                    />
                  </td>
                  <td className="px-3.5 py-3">
                    <Link
                      to={`/meetings/${m.id}/edit`}
                      className="font-medium text-charcoal hover:text-pine"
                    >
                      {m.title || (
                        <span className="italic text-charcoal/45">Untitled</span>
                      )}
                    </Link>
                    <div className="text-[10px] text-charcoal/45">
                      Updated {new Date(m.updatedAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-3.5 py-3 text-[12px] tabular-nums text-charcoal">
                    {m.meetingDate}
                  </td>
                  <td className="px-3.5 py-3 text-[12px] text-charcoal">
                    {m.projectName ?? '-'}
                  </td>
                  <td className="px-3.5 py-3">
                    <div className="flex items-center gap-2">
                      <AttendeeAvatar
                        name={m.ownerName ?? m.ownerEmail ?? '?'}
                        headshotUrl={m.ownerHeadshotUrl}
                        size={24}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-[12px] text-charcoal">
                          {m.ownerName ?? '-'}
                        </div>
                        <div className="truncate text-[10px] text-charcoal/50">
                          {m.ownerEmail ?? ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3.5 py-3 text-[11px]">
                    <StatusBadge status={m.status} />
                  </td>
                  <td className="px-3.5 py-3 text-right tabular-nums text-[12px] text-charcoal">
                    ${m.costUsd.toFixed(2)}
                  </td>
                  <td className="px-3.5 py-3 text-right">
                    {/* Mockup .tbl-action: bone bg + charcoal/15 +
                        rounded-full + 4/12 padding + 11px medium
                        charcoal/70, hover pine border/text + pine-soft
                        bg. Restore variant uses pine outline. */}
                    <div className="flex items-center justify-end gap-1.5">
                      {m.status === 'soft_deleted' ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              confirm(`Restore "${m.title}" to Draft?`)
                            ) {
                              restoreOne.mutate(m.id);
                            }
                          }}
                          disabled={restoreOne.isPending}
                          className="rounded-full border border-pine bg-transparent px-3 py-1 text-[11px] font-medium text-pine transition-colors hover:bg-pine/[0.08] disabled:opacity-50"
                        >
                          Restore
                        </button>
                      ) : (
                        <Link
                          to={`/meetings/${m.id}/edit`}
                          className="rounded-full border border-charcoal/15 bg-bone px-3 py-1 text-[11px] font-medium text-charcoal/70 no-underline transition-colors hover:border-pine hover:bg-pine/[0.08] hover:text-pine"
                        >
                          Open
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => setAuditForId(m.id)}
                        className="rounded-full border border-charcoal/15 bg-bone px-3 py-1 text-[11px] font-medium text-charcoal/70 transition-colors hover:border-pine hover:bg-pine/[0.08] hover:text-pine"
                      >
                        Audit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {/* Infinite-scroll sentinel */}
              {list.hasNextPage && (
                <tr ref={sentinelRef}>
                  <td
                    colSpan={8}
                    className="py-4 text-center text-[11px] italic text-charcoal/45"
                  >
                    {list.isFetchingNextPage ? 'Loading more…' : ' '}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {auditForId && (
        <AuditModal
          meetingId={auditForId}
          onClose={() => setAuditForId(null)}
        />
      )}

      {transferOpen && (
        <BulkTransferModal
          selectedCount={selectedIds.length}
          onCancel={() => setTransferOpen(false)}
          onConfirm={(newOwnerUserId) =>
            bulkTransfer.mutate({ ids: selectedIds, newOwnerUserId })
          }
          isPending={bulkTransfer.isPending}
          errMsg={
            bulkTransfer.error instanceof ApiError
              ? bulkTransfer.error.message
              : bulkTransfer.error instanceof Error
                ? bulkTransfer.error.message
                : null
          }
        />
      )}
    </div>
  );
}

/* ─── Status badge + chip ──────────────────────────────────────────── */

function StatusBadge({
  status,
}: {
  status: 'draft' | 'finalized' | 'soft_deleted';
}) {
  // Mockup .status-badge family: rounded-full + 10px bold uppercase
  // tracking-[0.10em] + 2/8 padding. Finalized → pine/10 + pine,
  // Deleted → terra-cotta-tinted (was red), Draft → charcoal/10
  // neutral.
  if (status === 'finalized') {
    return (
      <span className="inline-block rounded-full bg-pine/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] text-pine">
        Finalized
      </span>
    );
  }
  if (status === 'soft_deleted') {
    return (
      <span className="inline-block rounded-full bg-terra-cotta/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] text-terra-cotta">
        Deleted
      </span>
    );
  }
  return (
    <span className="inline-block rounded-full bg-charcoal/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] text-charcoal/70">
      Draft
    </span>
  );
}

function StatusChip({
  label,
  count,
  active,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone: 'default' | 'pine' | 'red';
  onClick: () => void;
}) {
  // Mockup .filter-chip family with tone-tinted active states:
  //   pine → .filter-chip--active-pine (pine fill cream text)
  //   red  → .filter-chip--active-red (terra fill, on-brand
  //          replacement for the mockup's #C44 hex)
  //   default → .filter-chip--active (pine fill — same as pine
  //             for the All chip; mockup uses a generic active)
  const activeCls =
    tone === 'pine'
      ? 'border-pine bg-pine text-cream'
      : tone === 'red'
        ? 'border-terra-cotta bg-terra-cotta text-white'
        : 'border-pine bg-pine text-cream';
  const idleCls =
    'border-charcoal/20 bg-cloud/60 text-charcoal hover:border-pine hover:text-pine';
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ' +
        (active ? activeCls : idleCls)
      }
    >
      {label}
      {/* Mockup .chip-count: ml-1.5 tabular-nums, opacity-80 when
          active. */}
      <span
        className={
          'ml-1.5 tabular-nums ' + (active ? 'opacity-80' : 'text-charcoal/50')
        }
      >
        {count}
      </span>
    </button>
  );
}

function PresetChip({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  // Mockup .preset-chip: cloud/60 + charcoal/20 + 11px charcoal,
  // hover pine border + pine text. Padding 3/12 (px-3 py-1).
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-charcoal/20 bg-cloud/60 px-3 py-1 text-[11px] text-charcoal transition-colors hover:border-pine hover:text-pine"
    >
      {children}
    </button>
  );
}

/* ─── Sortable header cell ─────────────────────────────────────── */

function Th({
  sortKey: key,
  current,
  dir,
  onSort,
  align = 'left',
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  // Mockup .admin-table thead th — 10px charcoal/65 bold uppercase
  // tracking-[0.14em] + 10/14 padding. Active sort col flips to pine.
  const isActive = current === key;
  return (
    <th
      className={
        'cursor-pointer select-none px-3.5 py-2.5 transition-colors hover:text-pine ' +
        (align === 'right' ? 'text-right ' : '') +
        (isActive ? 'text-pine' : '')
      }
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive && (
          <span className="text-[9px] leading-none">
            {dir === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </span>
    </th>
  );
}

/* ─── Owner picker (dropdown from /admin/users) ──────────────────── */

function OwnerPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const usersQ = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () =>
      api.get<{
        users: Array<{ id: string; displayName: string; email: string }>;
      }>('/admin/users'),
    staleTime: 60_000,
  });
  return (
    <label className="flex flex-col gap-1 text-[11px]">
      <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
        Owner
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-[180px] rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
      >
        <option value="">Any owner</option>
        {(usersQ.data?.users ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.displayName} - {u.email}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ─── Project picker (dropdown from /projects) ──────────────────── */

function ProjectPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const projectsQ = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () =>
      api.get<{ projects: Array<{ id: string; name: string }> }>('/projects', {
        limit: 500,
      }),
    staleTime: 60_000,
  });
  return (
    <label className="flex flex-col gap-1 text-[11px]">
      <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
        Project
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-[180px] rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
      >
        <option value="">Any project</option>
        {(projectsQ.data?.projects ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ─── Bulk-transfer modal ────────────────────────────────────────── */

function BulkTransferModal({
  selectedCount,
  onCancel,
  onConfirm,
  isPending,
  errMsg,
}: {
  selectedCount: number;
  onCancel: () => void;
  onConfirm: (newOwnerUserId: string) => void;
  isPending: boolean;
  errMsg: string | null;
}) {
  const usersQ = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () =>
      api.get<{
        users: Array<{
          id: string;
          displayName: string;
          email: string;
          isActive: boolean;
        }>;
      }>('/admin/users'),
  });
  const candidates = (usersQ.data?.users ?? []).filter((u) => u.isActive);
  const [newOwnerUserId, setNewOwnerUserId] = useState<string>(
    candidates[0]?.id ?? '',
  );
  useEffect(() => {
    if (!newOwnerUserId && candidates.length > 0) {
      setNewOwnerUserId(candidates[0].id);
    }
  }, [candidates, newOwnerUserId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Transfer ownership
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            Reassigns the owner_user_id on <b>{selectedCount}</b> meeting
            {selectedCount === 1 ? '' : 's'}. Creator history is preserved.
          </p>
        </div>
        <div className="px-5 py-4">
          {candidates.length === 0 ? (
            <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              No active users to transfer to.
            </div>
          ) : (
            <label className="flex flex-col gap-1.5 text-[11px]">
              <span className="font-bold uppercase tracking-[0.22em] text-pine/85">
                New owner
              </span>
              <select
                value={newOwnerUserId}
                onChange={(e) => setNewOwnerUserId(e.target.value)}
                className="rounded-[10px] border border-charcoal/15 bg-cream px-3 py-2 text-[13px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
              >
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName} - {c.email}
                  </option>
                ))}
              </select>
            </label>
          )}
          {errMsg && (
            <div className="mt-3 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
              {errMsg}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(newOwnerUserId)}
            disabled={!newOwnerUserId || isPending}
            className="rounded-full bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
          >
            {isPending ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Audit modal ────────────────────────────────────────────────── */

interface AuditEntry {
  id: number;
  action: string;
  before: unknown;
  after: unknown;
  createdAt: string;
  adminUserId: string;
  adminName: string | null;
  adminEmail: string | null;
}

function AuditModal({
  meetingId,
  onClose,
}: {
  meetingId: string;
  onClose: () => void;
}) {
  const auditQ = useQuery({
    queryKey: ['admin', 'meetings', 'audit', meetingId],
    queryFn: () =>
      api.get<{ entries: AuditEntry[] }>(`/admin/meetings/${meetingId}/audit`),
  });
  // Pulled to resolve owner-user-id refs in transfer entries to
  // display names. Cached via same query key the filter bar uses.
  const usersQ = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () =>
      api.get<{
        users: Array<{ id: string; displayName: string; email: string }>;
      }>('/admin/users'),
    staleTime: 60_000,
  });
  const userById = useMemo(() => {
    const m = new Map<string, { displayName: string; email: string }>();
    for (const u of usersQ.data?.users ?? []) {
      m.set(u.id, { displayName: u.displayName, email: u.email });
    }
    return m;
  }, [usersQ.data]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Audit log
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            Admin actions taken on this meeting. Edits in the editor
            aren't logged here - this is scoped to admin-only operations
            (restore, transfer, soft-delete).
          </p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-3 text-[13px]">
          {auditQ.isLoading && (
            <div className="py-4 text-center italic text-charcoal/55">
              Loading…
            </div>
          )}
          {auditQ.data && auditQ.data.entries.length === 0 && (
            <div className="py-4 text-center italic text-charcoal/55">
              No admin actions recorded for this meeting.
            </div>
          )}
          {auditQ.data && auditQ.data.entries.length > 0 && (
            <ul className="divide-y divide-charcoal/10">
              {auditQ.data.entries.map((e) => (
                <AuditEntryRow key={e.id} entry={e} userById={userById} />
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Audit entry row (humanized summary + collapsible JSON) ────── */

function AuditEntryRow({
  entry,
  userById,
}: {
  entry: AuditEntry;
  userById: Map<string, { displayName: string; email: string }>;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const summary = summarizeAudit(entry, userById);
  const hasDiff = !!entry.before || !!entry.after;
  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className="font-medium text-charcoal">{summary.label}</span>
        <span className="text-[11px] text-charcoal/50">
          {new Date(entry.createdAt).toLocaleString()}
        </span>
      </div>
      {summary.detail && (
        <div className="mt-0.5 text-[12px] text-charcoal/70">
          {summary.detail}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/55">
          by {entry.adminName ?? entry.adminEmail ?? '(unknown)'}
          <span className="ml-1.5 rounded-full bg-charcoal/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-charcoal/50">
            {entry.action}
          </span>
        </div>
        {hasDiff && (
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11px] text-charcoal/45 hover:text-pine"
          >
            {showRaw ? 'Hide raw' : 'Show raw'}
          </button>
        )}
      </div>
      {showRaw && hasDiff && (
        <pre className="mt-2 overflow-x-auto rounded-md bg-paper-light p-2 text-[10px] text-charcoal/70">
          {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
        </pre>
      )}
    </li>
  );
}

/** Humanize an audit entry into { label, detail }. Label is the
 *  primary noun-phrase (bold-weight), detail is the optional
 *  parenthetical with specifics (e.g. "from Andrew → Ethan"). */
function summarizeAudit(
  entry: AuditEntry,
  userById: Map<string, { displayName: string; email: string }>,
): { label: string; detail: string | null } {
  const nameFor = (id: string | null | undefined): string => {
    if (!id) return '(unknown)';
    const hit = userById.get(id);
    return hit ? hit.displayName : `user ${id.slice(0, 8)}…`;
  };

  switch (entry.action) {
    case 'meeting.restore':
    case 'meeting.bulk-restore':
      return {
        label: 'Restored to draft',
        detail: 'Soft-deleted meeting was returned to the drafts list.',
      };
    case 'meeting.bulk-soft-delete':
      return {
        label: 'Moved to trash',
        detail: 'Meeting was soft-deleted. Restorable from the Deleted filter.',
      };
    case 'meeting.bulk-transfer': {
      const before = (entry.before as { ownerUserId?: string } | null)
        ?.ownerUserId;
      const after = (entry.after as { ownerUserId?: string } | null)
        ?.ownerUserId;
      return {
        label: 'Ownership transferred',
        detail: `${nameFor(before)} → ${nameFor(after)}`,
      };
    }
    default:
      // Unknown action - just show the raw verb so we don't silently
      // lose new audit entries added later.
      return { label: entry.action, detail: null };
  }
}
